/*
#*===================================================================
#*
#* Licensed Materials - Property of GameTimeFootage Inc.
#*
#* Copyright NextPro 2018. All Rights Reserved.
#*
#*===================================================================
*/

import { ActionReducerMap, createFeatureSelector, createSelector } from '@ngrx/store';
import * as fromGetLibraryHighlights from 'app/library/store/reducers/get-library-highlights.reducer';
import * as fromGetLibraryGames from 'app/library/store/reducers/get-library-games.reducer';
import * as fromGetLibraryUploadedGames from 'app/library/store/reducers/get-library-uploaded-games.reducer';
import * as fromGetLibraryClips from 'app/library/store/reducers/get-library-clips.reducer';
import * as fromGetLibraryProFeedback from 'app/library/store/reducers/get-library-pro-feedback.reducer';
import * as fromSubmitHighlightForm from 'app/library/store/reducers/submit-highlight-form.reducer';
import * as fromGetBreakdownQueue from 'app/library/store/reducers/get-breakdown-queue.reducer';

export interface LibraryState {
  getLibraryHighlights: fromGetLibraryHighlights.GetLibraryHighlightsState;
  getLibraryGames: fromGetLibraryGames.GetLibraryGamesState;
  getLibraryUploadedGames: fromGetLibraryUploadedGames.GetLibraryUploadedGamesState;
  getLibraryClips: fromGetLibraryClips.GetLibraryClipsState;
  getLibraryProFeedback: fromGetLibraryProFeedback.GetLibraryProFeedbackState;
  submitHighlightForm: fromSubmitHighlightForm.SubmitHighlightFormState;
  getBreakdownQueue: fromGetBreakdownQueue.GetBreakdownQueueState;
}

export const reducers: ActionReducerMap<LibraryState> = {
  getLibraryHighlights: fromGetLibraryHighlights.reducer,
  getLibraryGames: fromGetLibraryGames.reducer,
  getLibraryUploadedGames: fromGetLibraryUploadedGames.reducer,
  getLibraryClips: fromGetLibraryClips.reducer,
  getLibraryProFeedback: fromGetLibraryProFeedback.reducer,
  submitHighlightForm: fromSubmitHighlightForm.reducer,
  getBreakdownQueue: fromGetBreakdownQueue.reducer,
};

export const getLibraryState = createFeatureSelector<LibraryState>('library');

// get highlights state
export const getLibraryHighlightsState = createSelector(
  getLibraryState,
  (state: LibraryState) => state.getLibraryHighlights
);

// get games state
export const getLibraryGamesState = createSelector(getLibraryState, (state: LibraryState) => state.getLibraryGames);

export const getLibraryUploadedGamesState = createSelector(
  getLibraryState,
  (state: LibraryState) => state.getLibraryUploadedGames
);

// get clips state
export const getLibraryClipsState = createSelector(getLibraryState, (state: LibraryState) => state.getLibraryClips);

// get breakdown queue state
export const getBreakdownQueueState = createSelector(getLibraryState, (state: LibraryState) => state.getBreakdownQueue);

// get submit highlight form state
export const submitHighlightFormState = createSelector(
  getLibraryState,
  (state: LibraryState) => state.submitHighlightForm
);
export const submitHighlightFormData = createSelector(
  submitHighlightFormState,
  fromSubmitHighlightForm.submitHighlightFormData
);
export const submitHighlightDraftData = createSelector(
  submitHighlightFormState,
  fromSubmitHighlightForm.submitHighlightDraftData
);

export const submitHighlightFormErrorMessage = createSelector(
  submitHighlightFormState,
  fromSubmitHighlightForm.submitHighlightFormErrorMessage
);
export const submitHighlightFormLoading = createSelector(
  submitHighlightFormState,
  fromSubmitHighlightForm.submitHighlightFormLoading
);
export const submitHighlightFormSuccess = createSelector(
  submitHighlightFormState,
  fromSubmitHighlightForm.submitHighlightFormSuccess
);

// get pro feedback state
export const getLibraryProFeedbackState = createSelector(
  getLibraryState,
  (state: LibraryState) => state.getLibraryProFeedback
);

export const getLibraryProFeedbackData = createSelector(
  getLibraryProFeedbackState,
  fromGetLibraryProFeedback.getProFeedbackData
);
